// External Imports
import { useContext, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import Radio from "../UI/Radio";
import { UtilityContext } from "../../context/util-context";
import { useLocation } from "react-router-dom";
import DateFilter from "../UI/DateFilter";
import FilterMenu from "../UI/FilterMenu";
import { requestAdminAPI } from "../../api/posh-api";
import { MdRefresh } from "react-icons/md";
import PaginationMenu from "../UI/PaginationMenu";
import { isoDateToString } from "../../util/format-date";
import { formatCurrency } from "../../util/format-text";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import ExportCsvButton from "../UI/ExportCsvButton";

// Internal Imports

/** ========================================================================
 * * Billing Page Component
 * This component renders a page for the billing to team.
 * ========================================================================== */

export default function BillingPage() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    type: "active",
    status: "pending",
    date: "dueDate",
    limit: "",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [totals, setTotals] = useState({
    pending: 0,
    pastDue: 0,
    delinquent: 0,
  });

  // Filter Options
  const filterOptions = [
    {
      label: "Date",
      options: [
        { label: "Due Date", value: "dueDate" },
        { label: "Date Created", value: "dateCreated" },
      ],
      selected: filter.date,
    },
  ];

  /* ====================== FETCH RECORDS ====================== */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      let filters = [`status: { ne: "succeeded" }`];
      if (filter.sd) filters.push(`${filter.date}: { ge: "${filter.sd}"}`);
      if (filter.type === "active")
        filters.push(`customer: {activeBookingId: { ne: null }}`);
      if (filter.type === "inactive")
        filters.push(`customer: {activeBookingId: { eq: null }}`);
      if (filter.status === "past_due") {
        const currentDate = new Date().toISOString();
        filters.push(`dueDate: { lt: "${currentDate}" }`);
      } else if (filter.status === "delinquent")
        filters.push(`status: { eq: "delinquent" }`);
      else if (filter.ed) filters.push(`${filter.date}: { le: "${filter.ed}"}`);

      const response = await requestAdminAPI("filter", {
        table: "payment",
        filters: filters,
        columns: [
          "customer { firstName lastName email phone id activeBooking { startDate endDate id vehicle { vin model year } } notes: notes_on_customer {note dateCreated employee { name }}}",
        ],
      });
      setPayments(response);

      // Create Customers List
      let customerMap = {};
      const newTotals = { pending: 0, pastDue: 0, delinquent: 0 };
      response.forEach((p) => {
        const customerId = p.customer?.id;
        if (customerMap[customerId]) {
          customerMap[customerId].total += p.amount;
          customerMap[customerId].payments.push(p);
          if (p.customer.activeBooking)
            customerMap[customerId].booking = p.customer.activeBooking;
        } else {
          customerMap[customerId] = {
            firstName: p.customer?.firstName,
            lastName: p.customer?.lastName,
            id: customerId,
            email: p.customer?.email,
            phone: p.customer?.phone,
            total: p.amount,
            pending: 0,
            pastDue: 0,
            delinquent: 0,
            payments: [p],
            booking: p.customer.activeBooking,
            notes: p.customer.notes.sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated)),
          };
        }
        if (p.status === "delinquent") {
          customerMap[customerId].delinquent += p.amount;
          newTotals.delinquent += p.amount;
        } else if (new Date(p.dueDate) < new Date()) {
          customerMap[customerId].pastDue += p.amount;
          newTotals.pastDue += p.amount;
        } else {
          customerMap[customerId].pending += p.amount;
          newTotals.pending += p.amount;
        }
      });
      setCustomers(
        Object.values(customerMap).sort((a, b) => b.total - a.total)
      );
      setTotals(newTotals);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <Heading
        title="Billing"
        text="Orange means customer has past due invoices, red means they have delinquent invoices and yellow means they only have pending invoices that are not due yet. Clicking on a row will open customer payments in a new tab."
      >
        <DateFilter
          onSubmit={(d) =>
            setFilter({ ...filter, sd: d.startDate, ed: d.endDate })
          }
          startDate={filter.sd}
          endDate={filter.ed}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <MdRefresh className="icon" onClick={fetchRecords} />
        <ExportCsvButton array={customers} filename="billing" />
      </Heading>

      <div className="grid grid-cols-6 gap-4">
        <Radio
          options={[
            { name: "Active Booking", id: "active" },
            { name: "No Booking", id: "inactive" },
            { name: "All Customers", id: "" },
          ]}
          onClick={(id) => setFilter({ ...filter, type: id })}
          selected={filter.type}
        />
        <Radio
          options={[
            { name: "All Pending", id: "pending" },
            { name: "Past Due", id: "past_due" },
            { name: "Delinquent", id: "delinquent" },
          ]}
          onClick={(id) => setFilter({ ...filter, status: id })}
          selected={filter.status}
        />
      </div>
      {loading ? (
        <LoadingSpinnerWrapper />
      ) : (
        <div className="list">
          {customers.map((customer, idx) => (
            <div
              key={idx}
              className={`grid grid-cols-6 gap-4 text-stone-200 pill ${
                customer.delinquent
                  ? "border-red-600"
                  : customer.pastDue
                  ? "border-orange-500"
                  : "border-yellow-600"
              }`}
              onClick={(e) =>
                navigate(
                  `/customers/payments?id=${customer.id}&returnTo=/billing`,
                  e,
                  {
                    newTab: true,
                  }
                )
              }
            >
              <div>
                <p className="font-semibold">
                  {customer.firstName} {customer.lastName}
                </p>
                <a
                  href={`tel:${customer.phone}`}
                  className="underline cursor-pointer text-stone-200"
                >
                  {customer.phone}
                </a>
              </div>
              {customer.booking ? (
                <div>
                  <p className="text-stone-200">
                    {customer.booking?.vehicle?.year}{" "}
                    {customer.booking?.vehicle?.model} -{" "}
                    {customer.booking?.vehicle?.vin.substring(13, 17)}
                  </p>
                  <p className="text-stone-400">
                    {isoDateToString(customer.booking?.startDate)} -{" "}
                    {isoDateToString(customer.booking?.endDate)}
                  </p>
                </div>
              ) : (
                <p className="text-stone-400">No Active Booking</p>
              )}
              {customer.notes?.length > 0 ? (
                <div className="lg:col-span-2">
                  <p>"{customer.notes.at(-1).note}..."</p>
                  <p className="text-stone-400">
                    {" "}
                    - {customer.notes.at(-1).employee?.name} (
                    {isoDateToString(customer.notes.at(-1).dateCreated)})
                  </p>
                </div>
              ) : (
                <div className="lg:col-span-2 text-stone-400">No Notes</div>
              )}
              <div className="flex space-x-4 text-lg font-semibold">
                <p className="text-yellow-600">
                  {formatCurrency(customer.pending)}
                </p>{" "}
                <p className="text-orange-500">
                  {formatCurrency(customer.pastDue)}
                </p>{" "}
                <p className="text-red-500">
                  {formatCurrency(customer.delinquent)}
                </p>
              </div>
              <p className="ml-auto text-xl font-semibold">
                {formatCurrency(customer.total)}
              </p>
            </div>
          ))}
        </div>
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(l) => setFilter({ ...filter, limit: l })}
        loading={loading}
        count={customers.length}
      >
        <p className="text-stone-400">
          Pending:{" "}
          <span className="font-semibold text-stone-200">
            {formatCurrency(totals.pending)}
          </span>
        </p>
        <p className="text-stone-400">
          Past Due:{" "}
          <span className="font-semibold text-stone-200">
            {formatCurrency(totals.pastDue)}
          </span>
        </p>
        <p className="text-stone-400">
          Delinquent:{" "}
          <span className="font-semibold text-stone-200">
            {formatCurrency(totals.delinquent)}
          </span>
        </p>
      </PaginationMenu>
    </div>
  );
}
